import { useState } from 'react';
import { Download, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useInstallPrompt } from './useInstallPrompt';
import { useServiceWorker } from './useServiceWorker';

/**
 * The two things the PWA ever interrupts someone about.
 *
 * Both are dismissible, both are keyboard reachable, and neither takes focus on
 * appearing — an alert that steals focus mid-sentence is worse than the thing it is
 * announcing. They sit at the bottom of the viewport, above the mobile navigation and
 * inside the safe area.
 */

/**
 * "A new version is available."
 *
 * The dirty-form guard is the part that matters: reloading discards whatever is in an
 * open form, so when one is dirty the prompt SAYS so and waits. `beforeunload` cannot
 * help here — this reload is ours, and the browser would not ask.
 */
export function UpdatePrompt() {
  const { updateReady, applyUpdate, dismissUpdate } = useServiceWorker();
  const [applying, setApplying] = useState(false);

  if (!updateReady) return null;

  const dirty = hasDirtyForm();

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-4 bottom-20 z-50 mx-auto max-w-md rounded-lg border border-line bg-raised p-4 shadow-lg sm:bottom-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <div className="flex items-start gap-3">
        <RefreshCw aria-hidden className="mt-0.5 size-5 shrink-0 text-action" />

        <div className="min-w-0 flex-1">
          <p className="font-medium">A new version is available</p>
          <p className="text-caption text-fg-muted">
            {dirty
              ? 'You have unsaved changes on this page. Save or discard them first — reloading now would lose them.'
              : 'Reloading takes a moment and picks up the latest fixes.'}
          </p>
        </div>

        <button
          type="button"
          aria-label="Dismiss"
          className="rounded-xs p-1 text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          onClick={dismissUpdate}
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" onClick={dismissUpdate}>
          Later
        </Button>
        <Button
          variant="primary"
          loading={applying}
          disabled={dirty}
          onClick={() => {
            setApplying(true);
            void applyUpdate();
          }}
        >
          Reload now
        </Button>
      </div>
    </div>
  );
}

/**
 * Is any form on the page dirty?
 *
 * Read from the DOM rather than from a store, because forms live in feature modules and
 * a global "is anything dirty" register would be another thing to keep in step. The
 * form system marks its own container, so this stays a single selector.
 */
function hasDirtyForm(): boolean {
  if (typeof document === 'undefined') return false;
  return document.querySelector('[data-dirty="true"]') !== null;
}

/** "Install BM-Work." Offered from the third visit, once. */
export function InstallPrompt() {
  const { shouldOffer, install, dismiss } = useInstallPrompt();

  if (!shouldOffer) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-4 bottom-20 z-40 mx-auto max-w-md rounded-lg border border-line bg-raised p-4 shadow-lg sm:bottom-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <div className="flex items-start gap-3">
        <Download aria-hidden className="mt-0.5 size-5 shrink-0 text-action" />

        <div className="min-w-0 flex-1">
          <p className="font-medium">Install BM-Work</p>
          <p className="text-caption text-fg-muted">
            It opens in its own window and starts on your dashboard. You can always
            install it later from Settings.
          </p>
        </div>
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" onClick={dismiss}>
          Not now
        </Button>
        <Button variant="primary" onClick={() => void install()}>
          Install
        </Button>
      </div>
    </div>
  );
}
